import React, { FC, useCallback, useState } from 'react';
import { Toolbox } from 'src/components/Toolbox/Toolbox';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import theme from 'src/config/theme';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { getContent } from './TOCToolboxHelper';
import { ContentItem } from 'src/components/Toolbox/type';
import SpeechBubbleTooltip from 'src/components/SpeechBubbleTooltip/SpeechBubbleTooltip';
import { TOCToolboxTypes } from './types';
import { useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { useGetTableOfContentQuery } from 'src/store/table-of-contents/tableOfContentsApi';

export const TOCToolbox: FC = () => {
  const log = useDataLogging();
  const [open, setOpen] = useState<TOCToolboxTypes>(null);
  let params = useParams<UrlParams>();
  const { bookId } = params;

  const { data } = useGetTableOfContentQuery(bookId ?? '', {
    skip: !bookId,
  });

  const handlePrintBook = useCallback(() => {
    window.open(data?.pdfUrl, '_blank')?.focus();
    log(LogActionTypes.PRINT_BOOK_REDIRECT, { bookId: bookId, bookPdfUrl: data?.pdfUrl });
    setOpen(null);
  }, [data?.pdfUrl, log, bookId]);

  const handleTooltipClick = useCallback(
    (type: TOCToolboxTypes, isHover?: boolean) => {
      if (type === 'print' && data?.pdfUrl && !isHover) {
        handlePrintBook();
      }
      if (type === open) {
        return setOpen(null);
      }
      return setOpen(type);
    },
    [data?.pdfUrl, handlePrintBook, open]
  );

  const getTooltipProps = useCallback(
    (type: TOCToolboxTypes) => {
      return {
        disableHoverListener: true,
        title: getContent(type),
        onMouseLeave: () => setOpen(null),
        onMouseEnter: () => handleTooltipClick(type, true),
        onClick: () => handleTooltipClick(type),
        open: open === type,
      };
    },
    [handleTooltipClick, open]
  );

  const topContent: ContentItem[] = [
    {
      id: 1,
      button: (
        <SpeechBubbleTooltip {...getTooltipProps('chart')}>
          <FontAwesomeSpriteIcon
            isDisabled
            color={theme.palette.secondary.main}
            icon={SpriteIconsLight.PROGRESS_AND_SCORES}
            iconSize="lg"
          />
        </SpeechBubbleTooltip>
      ),
    },
    {
      id: 2,
      button: (
        <SpeechBubbleTooltip {...getTooltipProps('search')}>
          <FontAwesomeSpriteIcon
            isDisabled
            color={theme.palette.secondary.main}
            icon={SpriteIconsLight.MAGNIFYING_GLASS}
            iconSize="lg"
          />
        </SpeechBubbleTooltip>
      ),
    },
    {
      id: 3,
      button: (
        <SpeechBubbleTooltip {...getTooltipProps('lock')}>
          <FontAwesomeSpriteIcon
            isDisabled
            color={theme.palette.secondary.main}
            icon={SpriteIconsLight.LOCK}
            iconSize="lg"
          />
        </SpeechBubbleTooltip>
      ),
    },
  ];

  const bottomContent: ContentItem[] = [
    {
      id: 4,
      button: (
        <SpeechBubbleTooltip {...getTooltipProps('donwload')} placement="left-end">
          <FontAwesomeSpriteIcon
            isDisabled
            color={theme.palette.secondary.main}
            icon={SpriteIconsLight.CLOUD_ARROW_DOWN}
            iconSize="lg"
          />
        </SpeechBubbleTooltip>
      ),
    },
    {
      id: 5,
      button: (
        <SpeechBubbleTooltip {...getTooltipProps('print')} placement="left-end">
          <FontAwesomeSpriteIcon
            isDisabled={!data?.pdfUrl}
            color={theme.palette.secondary.main}
            icon={SpriteIconsLight.PRINT}
            iconSize="lg"
          />
        </SpeechBubbleTooltip>
      ),
    },
  ];

  return <Toolbox topContent={topContent} bottomContent={bottomContent} />;
};
